import React, { useMemo } from 'react';
import cn from 'classnames';
import { MinusCircleIcon } from '@heroicons/react/20/solid';
import { AddCircleOutline } from '@mui/icons-material';
import { TreatmentTypeNativeEnum } from 'libs/end-to-end/src/treatment';
import { createTreatmentKey } from 'libs/end-to-end/src/treatment';
import { isTreatmentStatusSelected } from 'libs/end-to-end/src/treatment';
import { Treatment } from 'libs/end-to-end/src/treatment';
import { TREATMENT_MESSAGE_IDS } from 'apps/sigma2-ui/src/constants/data';
import { TreatmentRecommendationPill } from 'apps/sigma2-ui/src/components/ui/TreatmentRecommendationPill';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';
import { TreatmentMetaDetails } from 'apps/sigma2-ui/src/components/treatment/TreatmentMetaDetails';
import { useMetricConfig } from 'apps/sigma2-ui/src/lib/hooks/useMetricConfig';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

const TreatmentSummary: React.FC<{
  treatmentType: TreatmentTypeNativeEnum;
  focusArea: FocusArea;
  // It's just a switch statement, no need to fix
  // eslint-disable-next-line complexity
}> = ({ treatmentType, focusArea }) => {
  const variant = 'body-7';
  const language = useLanguage();

  switch (treatmentType) {
    case TreatmentTypeNativeEnum.ManagerBestPractices:
      return (
        <S2Typography
          variant={variant}
          translatedContent={language.treatmentManagerBestPracticesSummary({
            focusArea,
          })}
        />
      );
    case TreatmentTypeNativeEnum.IntelligentSourcingStrategy:
      return (
        <S2Typography
          translatedContent={language.treatmentIntelligentSourcingStrategySummary()}
          variant={variant}
        />
      );
    case TreatmentTypeNativeEnum.DiversityCopilot:
      return (
        <S2Typography
          translatedContent={language.treatmentDiversityCopilotSummary({
            focusArea,
          })}
          variant={variant}
        />
      );
    case TreatmentTypeNativeEnum.CompensationCopilot:
      return (
        <S2Typography
          translatedContent={language.treatmentCompensationCopilotSummary()}
          variant={variant}
        />
      );
    case TreatmentTypeNativeEnum.HiringCopilot:
      return (
        <S2Typography
          translatedContent={language.treatmentHiringCopilotSummary()}
          variant={variant}
        />
      );
    case TreatmentTypeNativeEnum.PromotionCopilot:
      return (
        <S2Typography
          translatedContent={language.treatmentPromotionCopilotSummary()}
          variant={variant}
        />
      );
    case TreatmentTypeNativeEnum.SourcingCopilot:
      return (
        <S2Typography
          translatedContent={language.treatmentSourcingCopilotSummary()}
          variant={variant}
        />
      );
    case TreatmentTypeNativeEnum.HiringProcessOptimization:
      return (
        <S2Typography
          translatedContent={language.treatmentHiringProcessOptimizationSummary()}
          variant={variant}
        />
      );
    case TreatmentTypeNativeEnum.PerformanceProcessOptimization:
      return (
        <S2Typography
          translatedContent={language.treatmentPerformanceProcessOptimizationSummary()}
          variant={variant}
        />
      );
    case TreatmentTypeNativeEnum.PromotionProcessOptimization:
      return (
        <S2Typography
          translatedContent={language.treatmentPromotionProcessOptimizationSummary()}
          variant={variant}
        />
      );
    case TreatmentTypeNativeEnum.TrainingStrategyCounterStereotype:
      return (
        <S2Typography
          translatedContent={language.treatmentTrainingStrategyCounterStereotypeSummary()}
          variant={variant}
        />
      );
    case TreatmentTypeNativeEnum.TrainingStrategyEmpathySkills:
      return (
        <S2Typography
          translatedContent={language.treatmentTrainingStrategyEmpathySkillsSummary()}
          variant={variant}
        />
      );
    case TreatmentTypeNativeEnum.SuccessPhenotype:
      return (
        <S2Typography
          translatedContent={language.treatmentSuccessPhenotypeSummary()}
          variant={variant}
        />
      );
    case TreatmentTypeNativeEnum.TrainingStrategyStructuralInequality:
      return (
        <S2Typography
          translatedContent={language.treatmentTrainingStrategyStructuralInequalitySummary()}
          variant={variant}
        />
      );
    case TreatmentTypeNativeEnum.ManagerUpskilling:
      return (
        <S2Typography
          translatedContent={language.treatmentManagerUpskillingSummary({
            focusArea,
          })}
          variant={variant}
        />
      );
    case TreatmentTypeNativeEnum.ProvideMoreData:
      return (
        <S2Typography
          translatedContent={language.treatmentProvideMoreDataSummary()}
          variant={variant}
        />
      );
    case TreatmentTypeNativeEnum.deprecated_BestPracticesMakingAlgorithms:
      return (
        <S2Typography
          translatedContent={language.treatmentBestPracticesAlgorithmSummary()}
          variant={variant}
        />
      );
    case TreatmentTypeNativeEnum.deprecated_InterGroupContact:
      return (
        <S2Typography
          translatedContent={language.treatmentInterGroupContactSummary()}
          variant={variant}
        />
      );
    case TreatmentTypeNativeEnum.deprecated_InterGroupContactTraining:
      return (
        <S2Typography
          translatedContent={language.treatmentInterGroupContactTrainingSummary()}
          variant={variant}
        />
      );
    default:
      throw guardExhaustive(treatmentType);
  }
};

const AddOrRemoveIcon: React.FC<{ isSelected: boolean }> = ({ isSelected }) => {
  if (isSelected) {
    <MinusCircleIcon className="w-7 h-6 text-gray-800" />;
  }
  return <AddCircleOutline className="w-6 h-6 text-secondary" />;
};

export const TreatmentCard = ({
  treatment,
  onSelect,
  disabled,
}: {
  treatment: Treatment;
  onSelect: (treatmentKey: string) => void;
  disabled: boolean;
}) => {
  const language = useLanguage();
  const treatmentName = TREATMENT_MESSAGE_IDS[treatment.type];
  const isSelected = isTreatmentStatusSelected(treatment.status);
  const treatmentKey = createTreatmentKey(treatment);

  const { getLevelByColumnValue } = useDimensionConfig();
  const { getMetricDisplayName } = useMetricConfig();

  const { myObservation } = useObservation();

  const LevelDefinition = getLevelByColumnValue(
    treatment.dimension,
    treatment.level,
  );

  const metricDisplayName = getMetricDisplayName({
    metricName: treatment.metric,
    focusArea: treatment.focusArea,
  });

  const focusAreaDefinition = useMemo(() => {
    return myObservation?.focusAreas.find(
      (focusArea) => focusArea.focusArea === treatment.focusArea,
    );
  }, [myObservation, treatment]);

  if (!focusAreaDefinition) {
    return null;
  }

  return (
    <div className="flex-1 relative max-w-sm">
      <button
        disabled={disabled}
        className={cn(
          'rounded-lg p-4 gap-4 border-1 outline-2 w-full h-full shadow-2px text-left transition-all relative will-change-transform shadow-0px hover:shadow-4px hover:-translate-y-1 flex flex-col justify-items-stretch',
          {
            'border-gray-200 hover:border-gray-500 hover:border-opacity-50':
              !isSelected,
            'outline-offset-0 border-secondary ': isSelected,
          },
        )}
        style={{
          outlineColor: 'hsla(270, 84%, 35%)',
          outlineWidth: '1px',
          outlineStyle: isSelected ? 'solid' : undefined,
        }}
        onClick={() => {
          onSelect(treatmentKey);
        }}
      >
        <div className="flex flex-col gap-1 w-full">
          <div className="flex flex-row justify-between text-center">
            <S2Typography
              variant="body-6"
              translatedContent={language[treatmentName]()}
              textColorVariant="gray-1000"
            />

            <AddOrRemoveIcon isSelected={isSelected} />
          </div>

          <S2Typography
            variant="body-7"
            translatedContent={language.treatmentCardImpactFactorLabel({
              impactFactor: treatment.impactFactor,
            })}
            textColorVariant="gray-800"
          />

          {metricDisplayName && (
            <S2Typography
              variant="body-7"
              translatedContent={language.treatmentCardMetricLabel({
                metricDisplayName,
              })}
              textColorVariant="gray-800"
            />
          )}

          {LevelDefinition && (
            <TreatmentMetaDetails
              levelDefinition={LevelDefinition}
              focusArea={focusAreaDefinition}
              biasType={treatment.biasType}
            />
          )}
        </div>

        <TreatmentSummary
          treatmentType={treatment.type}
          focusArea={treatment.focusArea}
        />

        <div className="flex flex-row gap-1 flex-wrap gap-y-4">
          {treatment.recommendations.map((treatmentRecommendation) => (
            <TreatmentRecommendationPill
              key={treatmentRecommendation}
              recommendation={treatmentRecommendation}
            />
          ))}
        </div>
      </button>
    </div>
  );
};
